import { cn } from '@/lib'
import { CommandInput } from 'cmdk'
import { Check, ChevronDown } from 'lucide-react'
import type React from 'react'
import { type HTMLProps, forwardRef, useState } from 'react'
import { Button } from '../Button'
import { Command, CommandItem, CommandList } from '../ui/command'
import { Input } from '../ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover'
import { Label } from './Label'

interface Item {
	value: string
	label: string
}

interface SelectTriggerProps {
	label: React.ReactNode
	items: Item[]
	placeholder?: string
	error?: React.ReactNode
	onValueChange?(): void
	onChange?(value: string): void
	disabled?: boolean
	value?: string | null
	dropdownClassName?: string
}

export const ComboBox: React.FC<SelectTriggerProps> = ({
	label,
	items,
	placeholder,
	onValueChange,
	disabled = false,
	error,
	onChange,
	value,
	dropdownClassName,
}) => {
	const [isOpen, setIsOpen] = useState(false)
	const [search, setSearch] = useState('')

	const onValChange = (val: string) => {
		onOpenChange(false)
		onChange?.(val)
		onValueChange?.()
	}

	const selectedItem = items.find((item) => item.value === value)

	const onOpenChange = (open: boolean) => {
		if (!open) {
			setSearch('')
		}
		setIsOpen(open)
	}

	return (
		<div className="flex flex-col gap-1">
			{label && <Label className="mb-1">{label}</Label>}
			<Popover open={isOpen} onOpenChange={onOpenChange}>
				<Command className="overflow-visible">
					<PopoverTrigger asChild>
						<CommandInput
							disabled={disabled}
							value={search}
							onValueChange={setSearch}
							onKeyDown={(e) => {
								if (e.key === 'Escape') {
									onOpenChange(false)
								} else if (!isOpen) {
									setIsOpen(true)
								}
							}}
							asChild
						>
							<ComboboxInput
								autoComplete="off"
								placeholder={placeholder}
								displayValue={selectedItem?.label || ''}
								isOpen={isOpen}
							/>
						</CommandInput>
					</PopoverTrigger>
					<PopoverContent
						align="start"
						className={cn('w-fit min-w-32', dropdownClassName)}
						onOpenAutoFocus={(e) => e.preventDefault()}
					>
						<CommandList>
							{items.map((item) => (
								<CommandItem
									key={item.value}
									value={item.label}
									onSelect={() => onValChange(item.value)}
									className={cn(
										'group flex cursor-default items-center gap-2 py-1.5 px-3 select-none data-[selected=true]:bg-orange-50',
										value === item.value && 'bg-purple-25',
									)}
								>
									<Check
										className={cn(
											'invisible size-4 fill-black',
											value === item.value && 'visible',
										)}
									/>
									<div className="text-sm/6 text-black">{item.label}</div>
								</CommandItem>
							))}
						</CommandList>
					</PopoverContent>
				</Command>
			</Popover>
			{error && <p className="text-red-500 text-sm">{error}</p>}
		</div>
	)
}

const ComboboxInput = forwardRef<
	HTMLInputElement,
	HTMLProps<HTMLInputElement> & {
		isOpen: boolean
		displayValue: string
	}
>(({ isOpen, displayValue, ...props }, ref) => (
	<div className="relative">
		<Input
			className={cn(
				'invisible absolute inset-0 z-10 w-full h-full pointer-events-none bg-transparent',
				!isOpen && displayValue && 'visible',
			)}
			value={displayValue}
			readOnly
			tabIndex={-1}
			disabled={props.disabled}
		/>
		<Input
			ref={ref}
			className={cn(
				'w-full h-full rounded-sm text-lg font-medium',
				'py-[11px] pl-3 pr-2',
				!isOpen && 'text-transparent',
			)}
			{...props}
			placeholder={displayValue ? '' : props.placeholder}
		/>
		<Button
			className="absolute h-full inset-y-0 right-0 group pointer-events-none"
			type="button"
			variant="tertiary"
			tabIndex={-1}
			disabled={props.disabled}
		>
			<ChevronDown
				className={cn(
					'size-6 fill-gray-900 group-disabled:fill-gray-400',
					isOpen && 'rotate-180 transition-transform',
				)}
			/>
		</Button>
	</div>
))
